/**
 * Configuration du router Vue
 * Définit toutes les routes avec guards d'authentification
 */
import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Layout principal
import AppLayout from '@/components/layout/AppLayout.vue'

// Routes publiques
const publicRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/Login.vue'),
    meta: { public: true, title: 'Connexion' },
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: () => import('@/views/auth/AuthCallback.vue'),
    meta: { public: true },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/auth/ResetPassword.vue'),
    meta: { public: true, title: 'Réinitialisation du mot de passe' },
  },
  {
    path: '/devenir-mandataire',
    name: 'AgentOnboarding',
    component: () => import('@/views/public/AgentOnboarding.vue'),
    meta: { public: true, title: 'Devenir mandataire' },
  },
  {
    path: '/cgu',
    name: 'CGU',
    component: () => import('@/views/legal/CGU.vue'),
    meta: { public: true, title: 'Conditions Générales d\'Utilisation' },
  },
  {
    path: '/politique-de-confidentialite',
    name: 'Privacy',
    component: () => import('@/views/legal/Privacy.vue'),
    meta: { public: true, title: 'Politique de Confidentialité' },
  },
]

// Routes protégées (dans le layout)
const protectedRoutes = [
  {
    path: '/',
    component: AppLayout,
    children: [
      {
        path: '',
        redirect: '/dashboard',
      },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/Dashboard.vue'),
        meta: { title: 'Tableau de bord' },
      },
      {
        path: 'notaire',
        name: 'NotaryDashboard',
        component: () => import('@/views/notary/Dashboard.vue'),
        meta: { title: 'Espace notaire', notaryOnly: true },
      },
      {
        path: 'notaire/dossiers',
        name: 'NotaryCases',
        component: () => import('@/views/notary/Cases.vue'),
        meta: { title: 'Dossiers', notaryOnly: true },
      },
      {
        path: 'notaire/etude',
        name: 'NotaryOffice',
        component: () => import('@/views/notary/Office.vue'),
        meta: { title: 'Mon étude', notaryOnly: true },
      },
      {
        path: 'notaire/rdv',
        name: 'NotaryAppointments',
        component: () => import('@/views/notary/Appointments.vue'),
        meta: { title: 'Rendez-vous', notaryOnly: true },
      },
      {
        path: 'notaire/biens',
        name: 'NotaryProperties',
        component: () => import('@/views/notary/Properties.vue'),
        meta: { title: 'Biens', notaryOnly: true },
      },
      {
        path: 'notaire/biens/:id',
        name: 'NotaryPropertyDetail',
        component: () => import('@/views/notary/PropertyDetail.vue'),
        meta: { title: 'Fiche du bien', notaryOnly: true },
      },
      // Recherche globale
      {
        path: 'search',
        name: 'SearchResults',
        component: () => import('@/views/search/Index.vue'),
        meta: { title: 'Recherche' },
      },
      // Rendez-vous
      {
        path: 'rdv',
        name: 'RDV',
        component: () => import('@/views/rdv/Index.vue'),
        meta: { title: 'Rendez-vous' },
      },
      // Propriétés (routes statiques avant la dynamique :id)
      {
        path: 'properties',
        name: 'Properties',
        component: () => import('@/views/properties/Index.vue'),
        meta: { title: 'Biens' },
      },
      {
        path: 'properties/add',
        name: 'AddProperty',
        component: () => import('@/views/properties/PropertyForm.vue'),
        meta: { title: 'Ajouter un bien' },
      },
      {
        path: 'properties/prospection',
        name: 'Prospection',
        component: () => import('@/views/properties/Prospection.vue'),
        meta: { title: 'Prospection' },
      },
      {
        path: 'properties/estimations',
        name: 'Estimations',
        component: () => import('@/views/properties/Estimations.vue'),
        meta: { title: 'Estimations' },
      },
      {
        path: 'properties/seller/:id',
        name: 'SellerProperties',
        component: () => import('@/views/properties/SellerProperties.vue'),
        meta: { title: 'Biens du vendeur' },
      },
      {
        path: 'properties/buyers/:id/search-criteria',
        name: 'BuyerMatching',
        component: () => import('@/views/properties/BuyerMatching.vue'),
        meta: { title: 'Biens correspondants' },
      },
      {
        path: 'studies',
        name: 'Studies',
        component: () => import('@/views/properties/Studies.vue'),
        meta: { title: 'Études' },
      },
      {
        path: 'properties/buyers',
        name: 'Buyers',
        component: () => import('@/views/properties/Buyers.vue'),
        meta: { title: 'Acquéreurs' },
      },
      {
        path: 'properties/sellers',
        name: 'Sellers',
        component: () => import('@/views/properties/Sellers.vue'),
        meta: { title: 'Vendeurs' },
      },
      {
        path: 'properties/:id',
        name: 'PropertyDetail',
        component: () => import('@/views/properties/PropertyDetail.vue'),
        meta: { title: 'Détail du bien' },
      },
      // Contacts
      {
        path: 'contacts',
        name: 'Contacts',
        component: () => import('@/views/contacts/Index.vue'),
        meta: { title: 'Contacts' },
      },
      {
        path: 'contacts/:id',
        name: 'ContactDetail',
        component: () => import('@/views/contacts/ContactDetail.vue'),
        meta: { title: 'Détail du contact' },
      },
      // Mandats
      {
        path: 'mandates',
        name: 'Mandates',
        component: () => import('@/views/mandates/Index.vue'),
        meta: { title: 'Mandats' },
      },
      {
        path: 'mandates/:id',
        name: 'MandateDetail',
        component: () => import('@/views/mandates/MandateDetail.vue'),
        meta: { title: 'Détail du mandat' },
      },
      // Suivi
      {
        path: 'suivi/compromis',
        name: 'SalesAgreements',
        component: () => import('@/views/suivi/SalesAgreements.vue'),
        meta: { title: 'Compromis' },
      },
      {
        path: 'agents/commissions',
        name: 'Commissions',
        component: () => import('@/views/agents/Commissions.vue'),
        meta: { title: 'Commissions' },
      },
      // Agences (admin only)
      {
        path: 'agences',
        name: 'Agencies',
        component: () => import('@/views/agencies/Index.vue'),
        meta: { title: 'Agences', adminOnly: true },
      },
      {
        path: 'agences/:id',
        name: 'AgencyDetail',
        component: () => import('@/views/agencies/AgencyDetail.vue'),
        meta: { title: 'Détail agence', adminOnly: true },
      },
      {
        path: 'fees',
        name: 'Fees',
        component: () => import('@/views/agencies/Fees.vue'),
        meta: { title: 'Honoraires', adminOnly: true },
      },
      // Agents (admin only)
      {
        path: 'agents',
        name: 'Agents',
        component: () => import('@/views/agents/Index.vue'),
        meta: { title: 'Agents', adminOnly: true },
      },
      {
        path: 'agents/:id',
        name: 'AgentDetail',
        component: () => import('@/views/agents/AgentDetail.vue'),
        meta: { title: 'Détail agent', adminOnly: true },
      },
      // Études notariales et notaires (admin only)
      {
        path: 'etudes-notariales',
        name: 'NotaryOfficesAdmin',
        component: () => import('@/views/notaries/Offices.vue'),
        meta: { title: 'Études notariales', adminOnly: true },
      },
      {
        path: 'notaires',
        name: 'NotariesAdmin',
        component: () => import('@/views/notaries/Members.vue'),
        meta: { title: 'Notaires', adminOnly: true },
      },
      // Comptes (admin only)
      {
        path: 'accounts',
        name: 'Accounts',
        component: () => import('@/views/accounts/Index.vue'),
        meta: { title: 'Comptes', adminOnly: true },
      },
      {
        path: 'accounts/:id',
        name: 'AccountDetail',
        component: () => import('@/views/accounts/AccountDetail.vue'),
        meta: { title: 'Détail compte', adminOnly: true },
      },
      // Paramètres
      {
        path: 'availability/weekly',
        name: 'WeeklyAvailability',
        component: () => import('@/views/settings/WeeklyAvailability.vue'),
        meta: { title: 'Disponibilités hebdomadaires' },
      },
      {
        path: 'availability/vacation',
        name: 'Vacation',
        component: () => import('@/views/settings/Vacation.vue'),
        meta: { title: 'Périodes de vacances' },
      },
      {
        path: 'secteurs',
        name: 'Sectors',
        component: () => import('@/views/sectors/Sectors.vue'),
        meta: { title: 'Secteurs' },
      },
      {
        // Ancienne URL « Codes postaux » → onglet de la rubrique Secteurs.
        path: 'zip-codes',
        redirect: { path: '/secteurs', query: { tab: 'codes' } },
      },
      // Parrainage
      {
        path: 'sponsorship',
        name: 'Sponsorship',
        component: () => import('@/views/sponsorship/Index.vue'),
        meta: { title: 'Parrainage' },
      },
      // Statistiques (admin only)
      {
        path: 'statistics',
        name: 'Statistics',
        component: () => import('@/views/statistics/Index.vue'),
        meta: { title: 'Statistiques', adminOnly: true },
      },
      // Compte utilisateur
      {
        path: 'account',
        name: 'Account',
        component: () => import('@/views/account/Index.vue'),
        meta: { title: 'Mon compte' },
      },
      {
        path: 'account/settings',
        name: 'AccountSettings',
        component: () => import('@/views/account/Settings.vue'),
        meta: { title: 'Paramètres du compte' },
      },
    ],
  },
]

// Route 404
const notFoundRoute = {
  path: '/:pathMatch(.*)*',
  name: 'NotFound',
  component: () => import('@/views/errors/NotFound.vue'),
  meta: { title: 'Page non trouvée' },
}

// Création du router
const router = createRouter({
  history: createWebHistory(),
  routes: [
    ...publicRoutes,
    ...protectedRoutes,
    notFoundRoute,
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    }
    return { top: 0, behavior: 'smooth' }
  },
})

// Guard global d'authentification
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Mise à jour du titre de page
  if (to.meta?.title) {
    document.title = `${to.meta.title} | Prix du Marché`
  }
  
  // Routes publiques - laisser passer
  if (to.meta?.public) {
    // Si déjà authentifié sur une route publique, rediriger vers dashboard
    if (authStore.isAuthenticated && to.path === '/login') {
      return next('/dashboard')
    }
    return next()
  }
  
  // Vérification de l'authentification
  // NB : `isAuthenticated` est persisté en localStorage donc peut être true
  // au démarrage alors que `user` n'a pas encore été chargé (après refresh).
  // Dans ce cas on rejoue checkAuthStatus pour repeupler user/session.
  if (!authStore.isAuthenticated || !authStore.user) {
    const isAuth = await authStore.checkAuthStatus()

    if (!isAuth) {
      return next({
        path: '/login',
        query: { redirect: to.fullPath },
      })
    }
  }
  
  // Vérification des droits admin
  if (to.meta?.adminOnly && !authStore.isAdmin) {
    return next('/dashboard')
  }

  if (authStore.isNotary && !to.meta?.notaryOnly) {
    return next('/notaire')
  }
  if (to.meta?.notaryOnly && !authStore.isNotary) {
    return next('/dashboard')
  }
  
  return next()
})

export default router
